/**
 * Currency conversion utilities for handling cents <-> dollars conversions.
 *
 * The backend stores all monetary values as int64 cents to avoid floating point errors.
 * These utilities provide consistent conversion between frontend (dollars) and backend (cents).
 */

/**
 * Convert dollar amount to cents.
 * Rounds to the nearest cent to handle floating point precision issues.
 *
 * @param dollars - Dollar amount as a decimal number (e.g., 19.99)
 * @returns Amount in cents as an integer (e.g., 1999)
 *
 * @example
 * dollarsToCents(19.99) // returns 1999
 * dollarsToCents(0.1 + 0.2) // returns 30 (handles floating point correctly)
 */
export function dollarsToCents(dollars: number): number {
  return Math.round(dollars * 100)
}

/**
 * Convert cents to dollar amount.
 *
 * @param cents - Amount in cents as an integer (e.g., 1999)
 * @returns Dollar amount as a decimal number (e.g., 19.99)
 *
 * @example
 * centsToDollars(1999) // returns 19.99
 * centsToDollars(100) // returns 1.00
 */
export function centsToDollars(cents: number): number {
  return cents / 100
}

/**
 * Format a dollar amount as a currency string without the currency symbol.
 * Always shows 2 decimal places.
 *
 * @param dollars - Dollar amount as a decimal number (e.g., 19.99)
 * @returns Formatted currency string without currency symbol (e.g., "19.99")
 *
 * @example
 * formatDollars(19.99) // returns "19.99"
 * formatDollars(1034) // returns "1,034.00"
 */
export function formatDollars(dollars: number): string {
  return dollars.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * Format cents as a currency string.
 * Always shows 2 decimal places.
 *
 * @param cents - Amount in cents as an integer (e.g., 1999)
 * @returns Formatted currency string without currency symbol (e.g., "19.99")
 *
 * @example
 * formatCurrency(1999) // returns "19.99"
 * formatCurrency(100) // returns "1.00"
 * formatCurrency(50) // returns "0.50"
 * formatCurrency(103400) // returns "1,034.00"
 */
export function formatCurrency(cents: number): string {
  return formatDollars(cents / 100)
}

/**
 * Format cents as a whole-dollar currency string — no cents, thousands
 * separators. For dense, at-a-glance surfaces (pipeline tiles/cards) where
 * exact cents aren't useful.
 *
 * @param cents - Amount in cents as an integer (e.g., 1999)
 * @returns Formatted whole-dollar string without currency symbol (e.g., "20")
 *
 * @example
 * formatCurrencyWhole(1999) // returns "20"
 * formatCurrencyWhole(103400) // returns "1,034"
 */
export function formatCurrencyWhole(cents: number): string {
  return Math.round(cents / 100).toLocaleString('en-US')
}

/**
 * Parse a currency string and return cents.
 * Handles strings with or without currency symbols, commas, etc.
 *
 * @param value - Currency string (e.g., "$19.99", "19.99", "1,999.99")
 * @returns Amount in cents as an integer (e.g., 1999)
 *
 * @example
 * parseCurrency("19.99") // returns 1999
 * parseCurrency("$19.99") // returns 1999
 * parseCurrency("1,999.99") // returns 199999
 */
export function parseCurrency(value: string): number {
  // Remove currency symbols, commas, and other non-numeric characters (except decimal point)
  const cleaned = value.replace(/[^0-9.-]/g, '')
  const parsed = parseFloat(cleaned)
  if (isNaN(parsed)) return 0
  return dollarsToCents(parsed)
}

const taxRateFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
})

/**
 * Format a tax rate percentage value (e.g. 8.5, 8.255) as a string with a
 * minimum of 2 and a maximum of 3 fraction digits, so a 3rd decimal only
 * surfaces when it's actually present.
 *
 * @param percentage - Tax rate as a percentage (e.g., 8.5 for 8.5%)
 * @returns Formatted percentage string without the "%" suffix (e.g., "8.50")
 *
 * @example
 * formatTaxRate(8.5) // returns "8.50"
 * formatTaxRate(8.25) // returns "8.25"
 * formatTaxRate(8.255) // returns "8.255"
 */
export function formatTaxRate(percentage: number): string {
  return taxRateFormatter.format(percentage)
}

/**
 * Format a tax rate fraction (e.g. 0.0825) as a percentage string, using the
 * same 2-3 fraction digit precision as formatTaxRate.
 *
 * @param fraction - Tax rate as a fraction (e.g., 0.0825 for 8.25%)
 * @returns Formatted percentage string without the "%" suffix (e.g., "8.25")
 *
 * @example
 * formatTaxRateFraction(0.0825) // returns "8.25"
 */
export function formatTaxRateFraction(fraction: number): string {
  return formatTaxRate(fraction * 100)
}
